import { JellyfinItem, JellyfinTrickplayInfo, JellyflixTrickplayManifest } from "../api/jellyfin";

export type TrickplayTile = {
  imageIndex: number;
  column: number;
  row: number;
  columns: number;
  rows: number;
  width: number;
  height: number;
};

export type GeneratedTrickplayTile = TrickplayTile & { url: string; key: string };

export function getGeneratedTrickplayTile(
  seconds: number,
  manifest?: JellyflixTrickplayManifest,
): GeneratedTrickplayTile | null {
  const milliseconds = Math.max(0, seconds * 1000);
  const candidates = (manifest?.sheets ?? []).filter((sheet) =>
    sheet.intervalMs > 0 && sheet.cols > 0 && sheet.rows > 0 &&
    sheet.thumbWidth > 0 && sheet.thumbHeight > 0 &&
    milliseconds >= sheet.startMs && milliseconds < sheet.endMs,
  );
  const sheet = candidates[0];
  if (!sheet) return null;
  const capacity = sheet.cols * sheet.rows;
  const available = Math.max(1, Math.min(capacity, sheet.availableThumbs ?? capacity));
  const thumbnailIndex = Math.min(
    available - 1,
    Math.max(0, Math.floor((milliseconds - sheet.startMs) / sheet.intervalMs)),
  );
  return {
    imageIndex: sheet.index,
    column: thumbnailIndex % sheet.cols,
    row: Math.floor(thumbnailIndex / sheet.cols),
    columns: sheet.cols,
    rows: sheet.rows,
    width: sheet.thumbWidth,
    height: sheet.thumbHeight,
    url: sheet.url,
    key: `${sheet.pass}:${sheet.index}:${thumbnailIndex}`,
  };
}

export function selectTrickplayInfo(
  trickplay: JellyfinItem["Trickplay"],
  mediaSourceId?: string,
): JellyfinTrickplayInfo | null {
  if (!trickplay) return null;
  const sourceEntries = mediaSourceId && trickplay[mediaSourceId]
    ? trickplay[mediaSourceId]
    : Object.values(trickplay)[0];
  if (!sourceEntries) return null;
  const entries = Object.values(sourceEntries).filter(
    (entry) => (entry.Width ?? 0) > 0 && (entry.TileWidth ?? 0) > 0 && (entry.TileHeight ?? 0) > 0,
  );
  if (!entries.length) return null;
  return entries.reduce((best, entry) => {
    const bestDistance = Math.abs((best.Width ?? 320) - 320);
    const nextDistance = Math.abs((entry.Width ?? 320) - 320);
    return nextDistance < bestDistance ? entry : best;
  });
}

export function getTrickplayTile(
  seconds: number,
  info: JellyfinTrickplayInfo,
): TrickplayTile | null {
  const interval = info.Interval ?? 0;
  const columns = info.TileWidth ?? 0;
  const rows = info.TileHeight ?? 0;
  const width = info.Width ?? 0;
  const height = info.Height ?? Math.round(width * 9 / 16);
  if (interval <= 0 || columns <= 0 || rows <= 0 || width <= 0 || height <= 0) return null;
  const thumbnailCount = Math.max(1, info.ThumbnailCount ?? Number.MAX_SAFE_INTEGER);
  const thumbnailIndex = Math.min(
    thumbnailCount - 1,
    Math.max(0, Math.floor((Math.max(0, seconds) * 1000) / interval)),
  );
  const tilesPerImage = columns * rows;
  const tileIndex = thumbnailIndex % tilesPerImage;
  return {
    imageIndex: Math.floor(thumbnailIndex / tilesPerImage),
    column: tileIndex % columns,
    row: Math.floor(tileIndex / columns),
    columns,
    rows,
    width,
    height,
  };
}
